"use client";

import { c as _c } from "react/compiler-runtime";
import { CustomImage } from "@repo/design-system/components/ui/custom-image";
import { cn } from "@repo/design-system/utils/cn";
import { type ReactNode, useEffect, useState } from "react";
import type { TranslationsMap } from "~/translations/loader/init";
import { useLanguage } from "~/translations/loader/use-language";
import type { JobadComponentProps } from "~/types/job";
import styles from "./application-process-eon-hungary.module.css";
interface StepDef {
  readonly icon: string;
  readonly textKey: keyof TranslationsMap;
}
const defaultSteps: StepDef[] = [{
  icon: "notification.svg",
  textKey: "hungary_applicationProcess_step1"
}, {
  icon: "checkbox_checked.svg",
  textKey: "hungary_applicationProcess_step2"
}, {
  icon: "talk_to_us.svg",
  textKey: "hungary_applicationProcess_step3"
}, {
  icon: "messenger.svg",
  textKey: "hungary_applicationProcess_step4"
}, {
  icon: "user_group_alt.svg",
  textKey: "hungary_applicationProcess_step5"
}, {
  icon: "handshake.svg",
  textKey: "hungary_applicationProcess_step6"
}];

// Trainee and Electrician skip step 5 and go straight to step 6
const shortSteps: StepDef[] = [{
  icon: "notification.svg",
  textKey: "hungary_applicationProcess_step1"
}, {
  icon: "checkbox_checked.svg",
  textKey: "hungary_applicationProcess_step2"
}, {
  icon: "talk_to_us.svg",
  textKey: "hungary_applicationProcess_step3"
}, {
  icon: "messenger.svg",
  textKey: "hungary_applicationProcess_step4"
}, {
  icon: "handshake.svg",
  textKey: "hungary_applicationProcess_step6"
}];
const SHORT_STEP_TEMPLATES = new Set(["HUN_Trainee", "HUN_Electrician"]);
const SHORT_STEP_HEADER_IMAGES = new Set(["images/header/subclients/hungary/01Gasfitters.webp", "images/header/subclients/hungary/00Electrician.webp"]);

// Step gap: mobile 96px, desktop 125px
const CONTAINER_HEIGHT: Record<number, string> = {
  5: "h-96 md:h-[500px]",
  6: "h-[480px] md:h-[625px]"
};
function Step(t0) {
  const $ = _c(20);
  const {
    children,
    isEven,
    index,
    length,
    bubbleStyle,
    boxStyle
  } = t0;
  const t1 = isEven ? "-translate-x-4 md:right-1/2 md:translate-x-4 md:flex-row-reverse md:text-end lg:translate-x-6" : "-translate-x-4 md:left-1/2 lg:-translate-x-6";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("absolute flex -translate-y-1/2 gap-4 **:transition-[background-color,color,scale,translate] **:duration-300 **:ease-in-out", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `${index * (100 / (length - 1))}%`;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      top: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== bubbleStyle) {
    t5 = cn("relative my-auto flex aspect-square h-8 w-8 items-center justify-center rounded-full lg:h-12 lg:w-12", bubbleStyle);
    $[4] = bubbleStyle;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = index + 1;
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = isEven && "md:py-4";
  let t9;
  if ($[9] !== boxStyle || $[10] !== t8) {
    t9 = cn("bg-gray-200 p-4 py-2 lg:p-6", t8, boxStyle);
    $[9] = boxStyle;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== children || $[13] !== t9) {
    t10 = <div className={t9}>{children}</div>;
    $[12] = children;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t2 || $[17] !== t4 || $[18] !== t7) {
    t11 = <div className={t2} style={t4}>{t7}{t10}</div>;
    $[15] = t10;
    $[16] = t2;
    $[17] = t4;
    $[18] = t7;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
}
export default function ApplicationProcessEonHungary(t0) {
  const $ = _c(16);
  const {
    job
  } = t0;
  const t = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const stepDefs = SHORT_STEP_TEMPLATES.has(job.data.template ?? "") || SHORT_STEP_HEADER_IMAGES.has(job.content.headerImage ?? "") ? shortSteps : defaultSteps;
  let t1;
  if ($[0] !== t) {
    t1 = def => ({
      icon: def.icon,
      text: t[def.textKey]
    });
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const steps = stepDefs.map(t1);
  useEffect(() => {
    let interval = null;
    const start = () => {
      interval = setInterval(() => {
        setActiveIndex(prev => (prev + 1) % steps.length);
      }, 2000);
    };
    const stop = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [steps.length]);
  const containerHeight = CONTAINER_HEIGHT[steps.length] ?? "h-[480px] md:h-[625px]";
  const t2 = "w-full overflow-hidden px-6 py-10 text-lg text-white md:px-10 md:py-20 md:text-2xl lg:text-3xl print:hidden";
  const t3 = "applicationProcess";
  const t4 = "mx-auto max-w-7xl";
  let t5;
  if ($[2] !== t.hungary_sectionBar_applicationProcess) {
    t5 = <h2 className="mb-16 font-bold text-3xl text-clientFont md:text-4xl lg:mb-24 lg:text-5xl">{t.hungary_sectionBar_applicationProcess}</h2>;
    $[2] = t.hungary_sectionBar_applicationProcess;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = "relative my-20 ml-4 w-full md:ml-0";
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="absolute top-0 bottom-0 -z-10 w-1 -translate-x-1/2 bg-secondary md:left-1/2" />;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== containerHeight) {
    t8 = cn("relative w-full", containerHeight);
    $[5] = containerHeight;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const t9 = steps.map((step, index) => <Step boxStyle={cn("md:rounded-md", {
    "bg-secondary": index !== activeIndex,
    "bg-primary text-headline_font_color": index === activeIndex && index !== steps.length - 1,
    "!bg-[#18A087] text-headline_font_color": index === activeIndex && index === steps.length - 1
  })} bubbleStyle={cn({
    "bg-secondary": index !== activeIndex,
    "scale-110 bg-primary text-headline_font_color": index === activeIndex && index !== steps.length - 1,
    "!bg-[#18A087] text-headline_font_color": index === activeIndex && index === steps.length - 1
  }, index === activeIndex && index !== steps.length - 1 && styles.bubble)} index={index} isEven={index % 2 === 0} key={index} length={steps.length}><div className="flex h-[72px] w-[250px] items-center justify-start gap-x-4 md:w-[410px] md:justify-center"><CustomImage alt="" className="shrink-0" height={32} src={`images/icons/hungary/${step.icon}`} width={32} />{step.text}</div></Step>);
  let t10;
  if ($[7] !== t8 || $[8] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[7] = t8;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t10 || $[11] !== t7) {
    t11 = <div className={t6}>{t7}{t10}</div>;
    $[10] = t10;
    $[11] = t7;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t11 || $[14] !== t5) {
    t12 = <section className={t2} id={t3}><div className={t4}>{t5}{t11}</div></section>;
    $[13] = t11;
    $[14] = t5;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  return t12;
}